/**
 * useEmailReputationData
 *
 * P3 Checkpoint 3.5D shell-phase hook for the Email "Reputation" view.
 *
 * Mirrors useEmailDeliverabilityData's shape (loading/error/data/refresh)
 * so swapping the mock adapter for a real read model later requires no
 * change to any consuming component.
 *
 * Deliberately accepts only `environment` — the one query concept this
 * page supports (see EmailFilterBar.jsx's EMAIL_FILTER_BAR_FIELDS
 * .environmentOnly, reused unchanged for this view). No period/program/
 * campaign/broadcast parameters are accepted merely for signature
 * symmetry with the other Email hooks — none has a defined meaning here.
 *
 * EMAIL-DELIVERY-TRACKING-004A/004B metric correction: deliveryRate/
 * bounceRate/deferredRate/complaintRate/unsubscribeRate remain mock-sourced
 * numbers in `summary` (unchanged, from emailReputationMockAdapter.js), but
 * this hook now ALSO fetches the real EDT dashboard summary and exposes
 * `rateAvailability` — a map of exactly the derived-rate keys that
 * endpoint's own `availability` block marks unavailable (all five of
 * delivery_rate/bounce_rate/deferred_rate/spam_complaint_rate/
 * unsubscribe_rate as of 004B), converted to the camelCase keys
 * EmailReputationView.jsx already renders. The view uses this to render
 * those KPIs as honestly unavailable instead of a fabricated percentage —
 * never by silently trusting the mock's own numbers once a real endpoint
 * exists to tell us they can't be truthfully computed.
 *
 * 004B note: deferredRate/complaintRate/unsubscribeRate have a DIFFERENT,
 * more severe gap than deliveryRate/bounceRate -- those two are missing
 * only a denominator (the numerator is a real EDT count); these three have
 * no live numerator at all (deferred_count/spam_report_at/unsubscribed_at
 * are schema-only, never populated by any current lifecycle projection).
 * The backend's own per-key `reason` string already reflects this
 * distinction accurately -- this hook only relays it, never rewrites it.
 *
 * The real fetch is independent/defensive (own try/catch, same pattern as
 * useEmailHygieneReconciliationActiveCount in EmailDeliverabilityView.jsx)
 * -- a failure here never blocks the rest of this (still mock-backed)
 * view from rendering; it just falls back to the pre-existing "estimated"
 * treatment for all five rates rather than surfacing an error.
 */

import { useState, useEffect, useCallback } from "react";
import { getMockEmailReputationData } from "../lib/emailReputationMockAdapter";
import { getEmailDeliveryDashboardSummary } from "../lib/emailDeliveryDashboardApi";

// Maps the backend's snake_case availability keys to the camelCase
// summary keys EmailReputationView.jsx's SUMMARY_KPI_DEFS already use.
const AVAILABILITY_KEY_MAP = {
  delivery_rate: "deliveryRate",
  bounce_rate: "bounceRate",
  deferred_rate: "deferredRate",
  spam_complaint_rate: "complaintRate",
  unsubscribe_rate: "unsubscribeRate",
};

async function fetchRateAvailability() {
  try {
    const summary = await getEmailDeliveryDashboardSummary();
    const availability = summary?.availability || {};
    const mapped = {};
    for (const [backendKey, frontendKey] of Object.entries(AVAILABILITY_KEY_MAP)) {
      const entry = availability[backendKey];
      if (entry) {
        mapped[frontendKey] = { status: "UNAVAILABLE", reason: entry.reason };
      }
    }
    return mapped;
  } catch (err) {
    // Defensive, independent of the mock fetch below -- never blocks the
    // rest of the Reputation view from rendering. Falls back to the
    // pre-existing "estimated" KPICard treatment for deliveryRate/
    // bounceRate, same as before this checkpoint.
    return {};
  }
}

// P3 shell phase: backed by the mock adapter for every section except the
// five-rate availability override above. When real
// reputation data sources exist for the remaining sections (Google
// Postmaster, Yahoo/Microsoft reputation integrations, IP inventory,
// domain authentication verification, blocklist/inbox-placement providers
// — see emailReputationMockAdapter.js's REPUTATION_DATA_READINESS),
// replace the rest of this function body — the hook's return shape and
// every consumer stay unchanged.
const fetchSource = async ({ environment }) => {
  const [mockData, rateAvailability] = await Promise.all([
    getMockEmailReputationData({ environment }),
    fetchRateAvailability(),
  ]);
  return { ...mockData, rateAvailability };
};

export function useEmailReputationData(options = {}) {
  const { environment = "all" } = options;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Depends only on `environment` -- fires exactly once per Apply, never
  // on a draft edit (same contract as useEmailDeliverabilityData).
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchSource({ environment });
      setData(result);
    } catch (err) {
      setError(err?.message || "Failed to load Reputation data");
    } finally {
      setLoading(false);
    }
  }, [environment]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    error,
    data,
    summary: data?.summary || null,
    mailboxProviders: data?.mailboxProviders || [],
    sendingIps: data?.sendingIps || [],
    authentication: data?.authentication || null,
    blacklistInboxPlacement: data?.blacklistInboxPlacement || null,
    environmentComparison: data?.environmentComparison || [],
    rateAvailability: data?.rateAvailability || {},
    refresh: fetchData,
  };
}

export default useEmailReputationData;
